import { createSelector } from 'reselect';
import { getConfig, getDataTypeConfig } from '@components/config/selectors';

const DEFAULT_ENTITY = {};
const DEFAULT_ENTITIES = [];
const DEFAULT_NEXT = '';

// ─── Pure state accessors ─────────────────────────────────────────────────────

export const getEntityType = (state, props) => props.type;
export const getAgencyTypes = state => state.dataTypes.agency_type;
// Raw runtime state — used only as an input to getCurrentEntityTypes.
const getAllEntities = state => state.entities.types;

export const getOnlyShowOverlaps = state => state.map.onlyShowOverlaps;
export const getOverlapEntities = state => state.dataDetail.overlapEntities;

// ─── Config-derived selectors ─────────────────────────────────────────────────

export const getEntityNames = createSelector(
  [getConfig],
  ({entities}) => entities
);

export const getEntityDefaultFilters = createSelector(
  [getConfig],
  config => config?.filters?.entities || {}
);

export const getTypesConfig = createSelector(
  [getConfig],
  config => {
    if (!config?.entity_types || !config?.filters?.entities) {
      return {};
    }
    const entityDict = {};
    config.entity_types.forEach(entityType => {
      entityDict[entityType.name] = entityType;
    });
    const typesConfig = {};
    (config.entities || []).forEach(type => {
      typesConfig[type] = {
        ...(entityDict[type]?.config || {}),
        defaultFilters: config.filters.entities[type] || {}
      };
    });
    return typesConfig;
  }
);

// Returns the initial per-type state shape for every entity type derived from
// config — mirrors what the removed FETCH_CONFIG_SUCCESS case built in the reducer.
export const getInitialEntityTypes = createSelector(
  [getConfig, getEntityDefaultFilters],
  (config, entityFilters) => {
    const types = {};
    (config?.entities || []).forEach(type => {
      types[type] = {
        filters: { ...(entityFilters[type] || {}) }
      };
    });
    return types;
  }
);

// Merges config-derived initial state for every entity type with any runtime
// updates the entities reducer has made (loaded lists, filter changes, etc.).
// Guarantees all configured types are present even before they are first fetched.
export const getCurrentEntityTypes = createSelector(
  [getInitialEntityTypes, getAllEntities],
  (initialTypes, runtimeTypes) => {
    const merged = {};
    Object.keys(initialTypes).forEach(type => {
      const runtime = runtimeTypes[type] || {};
      merged[type] = {
        list: [],
        next: '',
        queryViewport: null,
        inViewport: true,
        ...initialTypes[type],
        ...runtime,
        filters: Object.fromEntries(
          Object.entries({
            ...(initialTypes[type].filters || {}),
            ...(runtime.filters || {})
          }).filter(([, value]) => value !== false)
        )
      };
    });
    return merged;
  }
);

// ─── Per-entity input selectors ───────────────────────────────────────────────

export const getEntity = createSelector(
  [getCurrentEntityTypes, getEntityType],
  (mergedTypes, type) => mergedTypes[type] || DEFAULT_ENTITY
);
export const getEntities = createSelector(
  [getEntity],
  entity => entity.list || DEFAULT_ENTITIES
);
export const getEntityNext = createSelector(
  [getEntity],
  entity => entity.next || DEFAULT_NEXT
);
export const getLoadViewport = createSelector(
  [getEntity],
  entity => entity.queryViewport
);
export const getLoadViewportState = createSelector(
  [getEntity],
  entity => entity.inViewport
);

export const getAllEntityTypeConfigs = createSelector(
  [state => state, getEntityNames],
  (state, entities) => {
    const configs = {};
    entities.forEach(entityTypeName => {
      configs[entityTypeName] = getDataTypeConfig(state, 'entity', entityTypeName);
    });
    return configs;
  }
);

export const getEntityChipConfig = createSelector(
  [(state, entityType) => getDataTypeConfig(state, 'entity', entityType)],
  ({config: entityConfig = {}}) => {
    const { frontend: { card: { chips: chipsConfig = null } = {} } = {}, fields = [] } = entityConfig;
    if (chipsConfig) {
      return chipsConfig.map(chipConfig => {
        if (!(chipConfig.type || chipConfig.field)) {
          return {
            type: 'field',
            field: chipConfig,
            autoHide: true,
            fieldConfig: fields[chipConfig] || {}
          };
        }
        return {
          ...chipConfig,
          fieldConfig: fields[chipConfig.field] || {}
        };
      });
    }
    return null;
  }
);

/*
Details section of entity list item.
This can be customized in the databases entityType config column using frontend.list.detail.
Currently can display text fields, categories, and agency name.
Sample configuration.
{
  ...,
  "frontend": {
    ...,
    "list": {
      ...,
      "detail": [
        "entityId",           // Strings are treated as field values with no label and autoHide true.
        "entityPriority",
        "entityDateRange",
        {
          "type": "field",    // one of field, category, agency.
          "field": "pmac_id", // if type is field or category, the backend name for the data.
          "label": "PMAC ID", // prefix the data value with this label or leave empty for no label.
          "autoHide": true    // if true (default true), removes the detail element from display if the value is empty or null.
        }
      ]
    }
  }
}
*/
export const getEntityListDetailConfig = createSelector(
  [(state, entityType) => getDataTypeConfig(state, 'entity', entityType)],
  ({config: entityConfig = {}}) => {
    const { frontend: { list: { detail: detailsConfig = null } = {} } = {}, fields = [] } = entityConfig;

    if (detailsConfig) {
      return detailsConfig.map(detailConfig => {
        if (!(detailConfig.type || detailConfig.field)) {
          return {
            type: 'field',
            field: detailConfig,
            autoHide: true,
            fieldConfig: fields[detailConfig] || {}
          };
        }
        return {
          ...detailConfig,
          fieldConfig: fields[detailConfig.field] || {}
        };
      });
    }
    return [
      {
        type: 'field',
        field: 'id',
        label: 'ID',
        autoHide: true
      },
      {
        type: 'agency',
        autoHide: true
      }
    ];
  }
);
